import { useProjectState } from '@common/git/Project/hooks';
import { FROZEN_EMPTY_ARRAY } from '@prophecy/utils/constants';
import { usePersistentCallback } from '@prophecy/utils/react/hooks';
import { useQueryClient } from '@tanstack/react-query';

import { useGlobalVariables } from '../../context/globalConfig/securedConfigHook';
import { useUser } from '../../context/user';
import {
  makePostCall,
  useGraphQlQuery,
  useGraphQlQueryWithoutShareTokens,
  useRestMutation,
  XHRResponseType
} from '../../data/util';
import { copilotEndpoint } from '../../utils/getServerUrl';
import { isReplayMode, replayToken } from '../../utils/replayToken';
import { isShareMode, shareToken } from '../../utils/shareToken';
import { useIsCopilotEnabled } from '../Copilot/useIsCopilotEnabled';
import { getOnBoardingDetailsReplayQuery, getProjectReplaysQuery } from './queries';

export const REPLAY_QUERY_KEY = 'Replay_Builder';

export const REPLAY_SENTRY_TAG = 'replay.sentry';

async function setCommitMessage(payload: {
  userId: string;
  projectId: string;
  branch: string;
  pipelineNames: string[];
  commitId: string;
}): XHRResponseType<Boolean> {
  return makePostCall(`${copilotEndpoint}/kinesis/setCommitMessage`, payload);
}

export const useMapCurrentChatsToCommit = (pipelineNames: string[]) => {
  const userId = useUser().user._id;
  const queryClient = useQueryClient();
  const { currentBranch: branch, projectId } = useProjectState();
  const isCopilotEnabled = useIsCopilotEnabled();
  const setCommitMessageMutation = useRestMutation(
    (commitId: string) => setCommitMessage({ userId, projectId, branch, pipelineNames, commitId }),
    {
      retry: 3,
      retryDelay: 2000,
      onSuccess: (resp) => {
        queryClient.invalidateQueries([REPLAY_QUERY_KEY]);
      },
      sentryTag: `${REPLAY_SENTRY_TAG}.setChatId`
    }
  );

  return usePersistentCallback(async (commitId: string) => {
    if (!isCopilotEnabled) return;
    await setCommitMessageMutation.mutateAsync(commitId);
  });
};

export const useOnboardingDetailReplayQuery = ({
  onError,
  enabled = true
}: { enabled?: boolean; onError?: (error: unknown) => void } = {}) => {
  const isProfessionalEditionEnabled = useGlobalVariables().isProfessionalEditionEnabled;
  const hasReplayShareMode = isShareMode() || isReplayMode();
  const isGuestUserLoggedIn = Boolean(shareToken.loggedInUserId || replayToken.loggedInUserID);

  const isEnabled = hasReplayShareMode
    ? isProfessionalEditionEnabled && isGuestUserLoggedIn && enabled
    : isProfessionalEditionEnabled && enabled;

  // called for normal users or logged-in user in share link
  const { data, isInitialLoading: isOnboardingDetailsLoading } = useGraphQlQueryWithoutShareTokens(
    [REPLAY_QUERY_KEY, 'onboardingReplayQuery'],
    getOnBoardingDetailsReplayQuery,
    {
      enabled: isEnabled,
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      onError: onError
    }
  );
  const onboardingReplayDetails = data?.professionalOnboardingDetails ?? [];
  const defaultOnboardingProjectId = onboardingReplayDetails.find((detail) => detail.projectId)?.projectId;

  return {
    onboardingReplayDetails: onboardingReplayDetails,
    defaultOnboardingProjectId,
    defaultReplayUrl: onboardingReplayDetails?.[0]?.replay?.shareLink,
    defaultReplayId: onboardingReplayDetails?.[0]?.replay?.replayId,
    isOnboardingDetailsLoading
  };
};

export const useGetProjectReplaysQuery = ({
  projectUID,
  enabled = true
}: {
  projectUID: string;
  enabled?: boolean;
}) => {
  const isProfessionalEditionEnabled = useGlobalVariables().isProfessionalEditionEnabled;
  const { data, isInitialLoading: isProjectReplaysLoading } = useGraphQlQuery(
    [REPLAY_QUERY_KEY, 'ProjectReplaysQuery'],
    getProjectReplaysQuery,
    {
      enabled: isProfessionalEditionEnabled && Boolean(projectUID) && enabled,
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false
    },
    { projectUID }
  );

  return {
    projectReplays: data?.getProjectReplays || (FROZEN_EMPTY_ARRAY as NonNullable<typeof data>['getProjectReplays']),
    isProjectReplaysLoading
  };
};
